import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, TextField, Button, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '420px',
  backgroundColor: '#fff',
  borderRadius: '8px',
  overflow: 'hidden',
  outline: 'none',
});

const ModalHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '48px',
  padding: '0 24px',
  backgroundColor: '#262626',
});

const ModalTitle = styled(Typography)({ fontSize: '16px', fontWeight: 600, color: '#fff' });

const Body = styled(Box)({ display: 'flex', flexDirection: 'column', gap: '12px', padding: '24px', backgroundColor: '#fafbfc' });

const Hint = styled(Typography)({ fontSize: '12px', color: '#808080', lineHeight: 1.5 });

const SlotRow = styled(Box)({ display: 'flex', alignItems: 'center', gap: '8px' });

const Tilde = styled(Typography)({ fontSize: '14px', color: '#666' });

// 종료가 시작보다 빠르면 자정을 넘기는 익일 구간 (예 22:00 ~ 익일 02:00)
const NextDayBadge = styled(Box)({
  flexShrink: 0,
  padding: '2px 6px',
  borderRadius: '4px',
  backgroundColor: '#E8F5F6',
  color: '#0F7F85',
  fontSize: '11px',
  fontWeight: 600,
});

const SaveButton = styled(Button)({
  height: '44px',
  borderRadius: '8px',
  backgroundColor: '#4ab3bc',
  color: '#fff',
  fontSize: '15px',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: '#3a9aa2' },
  '&.Mui-disabled': { backgroundColor: '#B8DADB', color: '#fff' },
});

// RCU(80B54E79 계열)가 구간을 하나만 저장한다 — 여러 개 보내면 첫 구간만 남음 (2026-09-23)
const MAX_SLOTS = 1;
const isHHmm = v => /^\d{2}:\d{2}$/.test(v);
const isOvernight = s => isHHmm(s.start) && isHHmm(s.end) && s.end < s.start;

/**
 * 도어 인증 가능 시간 등록 — 회원이 문 앞에서 인증(QR·안면)할 수 있는 시간대. 원격 개방 명령은 이 시간과 무관.
 * @param {string} doorOpening - 기존 값 JSON 문자열 [{start,end}]
 * @param {function} onSave - (opening: [{start,end}]) => void
 */
const DoorOpeningModal = ({ open, onClose, doorOpening, onSave, isPending = false }) => {
  const { t } = useTranslation();
  const [slots, setSlots] = useState([{ start: '', end: '' }]);

  useEffect(() => {
    if (!open) return;
    try {
      const parsed = doorOpening ? JSON.parse(doorOpening) : [];
      setSlots(Array.isArray(parsed) && parsed.length > 0 ? parsed.slice(0, MAX_SLOTS).map(s => ({ start: s.start || '', end: s.end || '' })) : [{ start: '', end: '' }]);
    } catch {
      setSlots([{ start: '', end: '' }]);
    }
  }, [open, doorOpening]);

  const update = (i, key, value) => setSlots(prev => prev.map((s, idx) => (idx === i ? { ...s, [key]: value } : s)));
  const remove = i => setSlots(prev => (prev.length === 1 ? [{ start: '', end: '' }] : prev.filter((_, idx) => idx !== i)));
  const filled = slots.filter(s => s.start || s.end);
  // 종료 < 시작 = 익일 구간으로 허용. 같은 시각만 오류
  const valid = filled.length > 0 && filled.every(s => isHHmm(s.start) && isHHmm(s.end) && s.start !== s.end);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{t('도어 인증 가능 시간')}</ModalTitle>
          <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </ModalHeader>
        <Body>
          <Hint>{t('회원이 문 앞에서 QR·안면 인증을 할 수 있는 시간대입니다. 관리자 원격 개방(일시개방·항시열림)은 이 시간과 무관합니다.')}</Hint>
          <Hint>{t('종료 시각이 시작보다 빠르면 익일까지의 구간으로 등록됩니다. (예 22:00 ~ 익일 02:00)')}</Hint>
          {slots.map((slot, i) => (
            <SlotRow key={i}>
              <TextField type="time" size="small" value={slot.start} onChange={e => update(i, 'start', e.target.value)} sx={{ flex: 1 }} />
              <Tilde>~</Tilde>
              <TextField type="time" size="small" value={slot.end} onChange={e => update(i, 'end', e.target.value)} sx={{ flex: 1 }} />
              {isOvernight(slot) && <NextDayBadge>{t('익일')}</NextDayBadge>}
              <IconButton size="small" onClick={() => remove(i)} aria-label={t('삭제')}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </SlotRow>
          ))}
          <SaveButton disabled={!valid || isPending} onClick={() => onSave(filled)}>
            {isPending ? t('저장 중...') : t('저장')}
          </SaveButton>
        </Body>
      </ModalContainer>
    </Modal>
  );
};

export default DoorOpeningModal;
